import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebarContext } from "../../contexts/SidebarContext";
import Sidebar from "./Sidebar";

function ProfileDropdown({ user, onClose }) {
  const navigate  = useNavigate();
  const roleName  = user?.role?.replace("ROLE_", "").replace(/_/g, " ") ?? "Admin";

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div style={{
      position:"absolute", top:"calc(100% + 10px)", right:0,
      width:230,
      background:"var(--bg-card)",
      border:"1px solid var(--border)",
      borderRadius:12,
      boxShadow:"0 8px 32px rgba(0,0,0,0.12)",
      zIndex:200,
      overflow:"hidden",
    }}>
      {/* User info block */}
      <div style={{
        padding:"14px 16px",
        borderBottom:"1px solid var(--border)",
        background:"rgba(15,61,62,0.03)",
      }}>
        <div style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)", marginBottom:3 }}>
          {user?.email}
        </div>
        <div style={{
          display:"inline-block",
          fontSize:10, fontWeight:700,
          color:"#0F3D3E",
          background:"rgba(15,61,62,0.1)",
          border:"1px solid rgba(15,61,62,0.18)",
          padding:"2px 9px", borderRadius:20,
          textTransform:"uppercase", letterSpacing:"0.5px",
        }}>
          {roleName}
        </div>
      </div>

      {/* Menu items */}
      {[
        {
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1119.07 5.93M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          ),
          label:"My Profile",
          onClick: () => go("/profile"),
        },
        {
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a4 4 0 010 8m-4-4H3m5-4l-4 4 4 4"/>
            </svg>
          ),
          label:"Change Password",
          onClick: () => go("/change-password"),
        },
      ].map((item, i) => (
        <button
          key={i}
          onClick={item.onClick}
          style={{
            width:"100%", display:"flex", alignItems:"center", gap:10,
            padding:"11px 16px", background:"none", border:"none",
            cursor:"pointer", color:"var(--text-secondary)",
            fontSize:13, fontFamily:"var(--font-body)",
            textAlign:"left", transition:"background 0.15s",
            borderBottom: i === 0 ? "1px solid var(--border)" : "none",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(15,61,62,0.05)"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >
          <span style={{ color:"#0F3D3E", flexShrink:0 }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user }    = useAuth();
  const { collapsed } = useSidebarContext();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  const roleName = user?.role?.replace("ROLE_", "").replace(/_/g, " ") ?? "Admin";

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropOpen]);

  return (
    <div style={{ display:"flex", minHeight:"100vh" }}>
      <Sidebar />

      <div style={{
        marginLeft: collapsed ? "72px" : "var(--sidebar-w)",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        minWidth: 0,
      }}>

        {/* ── Top Header ── */}
        <header style={{
          height: "var(--header-h)",
          background: "#ffffff",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          position: "sticky",
          top: 0,
          zIndex: 50,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}>

          {/* ── LEFT — Welcome ── */}
          <div>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontSize: 20,
              fontWeight: 700,
              color: "var(--text-primary)",
              lineHeight: 1.2,
              margin: 0,
            }}>
              Hello, <span style={{ color:"#0F3D3E" }}>{roleName}</span>
            </h1>
            <p style={{ fontSize:12, color:"var(--text-muted)", marginTop:3, margin:0 }}>
              {new Date().toLocaleDateString("en-IN", {
                weekday:"long", year:"numeric", month:"long", day:"numeric",
              })}
            </p>
          </div>

          {/* ── RIGHT — Avatar only, dropdown on click ── */}
          <div ref={dropRef} style={{ position:"relative" }}>
            <button
              onClick={() => setDropOpen(o => !o)}
              title="Account"
              style={{
                width:38, height:38,
                borderRadius:"50%",
                background: dropOpen
                  ? "#0F3D3E"
                  : "linear-gradient(135deg, var(--teal-600), var(--teal-400))",
                border: dropOpen ? "2px solid #0F3D3E" : "2px solid transparent",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:14, fontWeight:700, color:"#fff",
                cursor:"pointer", flexShrink:0,
                boxShadow:"0 2px 8px rgba(26,138,138,0.3)",
                transition:"background 0.2s, box-shadow 0.2s",
                outline:"none",
              }}
              onMouseEnter={e => { if (!dropOpen) e.currentTarget.style.boxShadow = "0 4px 14px rgba(15,61,62,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(26,138,138,0.3)"; }}
            >
              {user?.email?.[0]?.toUpperCase() || "A"}
            </button>

            {dropOpen && (
              <ProfileDropdown
                user={user}
                onClose={() => setDropOpen(false)}
              />
            )}
          </div>

        </header>

        {/* ── Page Content ── */}
        <main style={{
          flex: 1,
          padding: "28px 32px",
          background: "var(--bg-base)",
          minWidth: 0,     
          overflow: "hidden",
        }}>
          {children}
        </main>

      </div>
    </div>
  );
}